import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, CalendarDays, Camera, CheckCircle2, ClipboardList, FileDiff, HardHat, Heart, Wallet } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { fileUrl, money } from "@/lib/api";
import { Loading } from "@/components/States";

const d = (iso) => (iso ? new Date(iso + "T12:00:00").toLocaleDateString("en-CA", { month: "short", day: "numeric" }) : "");
const signed = (v) => (v < 0 ? "−" : "+") + money(Math.abs(v || 0));

function Panel({ title, icon: Icon, to, action, children, testid, className = "" }) {
  return (
    <section className={`rounded-xl border bg-card p-5 ${className}`} data-testid={testid}>
      <header className="mb-3 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold"><Icon className="h-4 w-4 text-primary" strokeWidth={1.8} />{title}</h3>
        {to && <Link to={to} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid={`${testid}-link`}>{action || "Open"}<ArrowRight className="h-3 w-3" /></Link>}
      </header>
      {children}
    </section>
  );
}

const Muted = ({ children }) => <p className="text-sm text-muted-foreground">{children}</p>;

export default function OverviewTab({ projectId, go }) {
  const { user } = useAuth();
  const { data, loading } = useFetch(`/projects/${projectId}/overview`);
  if (loading || !data) return <Loading rows={3} />;
  const team = user.role !== "client";
  const b = data.budget || {};
  return (
    <div className="grid gap-4 lg:grid-cols-3" data-testid="overview-tab">
      <Panel title="Next 7 days" icon={CalendarDays} to={go("schedule")} action="Schedule" testid="ov-schedule" className="lg:col-span-2">
        {data.overdue.length > 0 && (
          <ul className="mb-3 space-y-1" data-testid="ov-overdue">{data.overdue.map((t) => <li key={t.id} className="flex items-center gap-2 text-sm text-destructive"><AlertTriangle className="h-3.5 w-3.5" />{t.title}<span className="ml-auto text-xs">was due {d(t.end_date || t.start_date)}</span></li>)}</ul>
        )}
        {data.upcoming.length ? (
          <ul className="divide-y" data-testid="ov-upcoming">{data.upcoming.map((t) => (
            <li key={t.id} className="flex items-center gap-3 py-2 text-sm"><span className="w-20 shrink-0 text-xs text-muted-foreground">{d(t.start_date)}{t.end_date && t.end_date !== t.start_date ? ` – ${d(t.end_date)}` : ""}</span><span className="truncate">{t.title}</span>{t.trade && <span className="ml-auto rounded-full bg-secondary px-2 py-0.5 text-[11px]">{t.trade}</span>}</li>
          ))}</ul>
        ) : <Muted>{data.schedule_total ? "Nothing scheduled this week." : "No schedule yet — add the first milestone."}</Muted>}
      </Panel>

      <Panel title={team ? "Awaiting the client" : "Waiting on you"} icon={FileDiff} to={team ? go("estimate") : undefined} action="Estimate" testid="ov-awaiting">
        {data.awaiting.length ? (
          <ul className="space-y-2" data-testid="ov-awaiting-list">{data.awaiting.map((c) => <li key={c.id} className="flex items-center justify-between gap-2 text-sm"><span className="truncate">CO-{String(c.number).padStart(2, "0")} {c.title}</span><span className="stat text-sm">{signed(c.amount)}</span></li>)}</ul>
        ) : <Muted>No change orders waiting.</Muted>}
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="ov-signoffs">
          {["preferences", "proposal"].map((k) => { const s = data.signoffs.find((x) => x.kind === k); return <span key={k} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${s ? "border-primary/30 bg-primary/5 text-primary" : "text-muted-foreground"}`}><CheckCircle2 className="h-3 w-3" />{k === "preferences" ? "Preferences" : "Proposal"} {s ? "approved" : "not yet approved"}</span>; })}
        </div>
        {team && data.open_wishes > 0 && <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground"><Heart className="h-3 w-3" />{data.open_wishes} wish list item{data.open_wishes > 1 ? "s" : ""} unanswered</p>}
        {data.planning_open > 0 && <Link to={go("prestart")} className={`mt-2 flex items-center gap-1.5 text-xs hover:underline ${data.planning_overdue ? "text-destructive" : "text-muted-foreground"}`} data-testid="overview-planning"><ClipboardList className="h-3 w-3" />{data.planning_open} pre-start planning item{data.planning_open > 1 ? "s" : ""} open{data.planning_overdue ? ` · ${data.planning_overdue} overdue` : ""}</Link>}
      </Panel>

      {team && (
        <Panel title="Money" icon={Wallet} to={go("estimate")} action="Estimate" testid="ov-money">
          {b.has_estimate ? (
            <dl className="grid grid-cols-3 gap-2 text-sm">
              <div><dt className="text-xs text-muted-foreground">Estimate</dt><dd className="stat text-lg" data-testid="ov-estimate-total">{money(b.total)}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Changes</dt><dd className={`stat text-lg ${b.approved_changes < 0 ? "text-emerald-700" : ""}`}>{signed(b.approved_changes)}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Contract</dt><dd className="stat text-lg" data-testid="ov-contract">{money(b.contract)}</dd></div>
            </dl>
          ) : <Muted>No estimate yet.{b.approved_changes ? ` Approved changes: ${signed(b.approved_changes)}` : ""}</Muted>}
        </Panel>
      )}

      {team && (
        <Panel title="On site this week" icon={HardHat} to={go("site")} action="Trades & Site" testid="ov-trades">
          {data.on_site.length ? (
            <ul className="space-y-1.5 text-sm" data-testid="ov-onsite-list">{data.on_site.map((t) => <li key={t.id} className="flex items-center justify-between"><span>{t.name}</span><span className="text-xs text-muted-foreground">{t.trade}</span></li>)}</ul>
          ) : <Muted>{data.trades_total ? "No trades scheduled this week." : "No trades added yet."}</Muted>}
        </Panel>
      )}

      <Panel title="Latest photos" icon={Camera} to={go("photos")} action="Photos" testid="ov-photos" className={team ? "" : "lg:col-span-2"}>
        {data.photos.length ? (
          <div className="grid grid-cols-3 gap-1.5" data-testid="ov-photo-grid">{data.photos.map((f) => <img key={f.id} src={fileUrl(f.url)} alt="" className="aspect-square w-full rounded-md object-cover" loading="lazy" />)}</div>
        ) : <Muted>No photos yet.</Muted>}
      </Panel>
    </div>
  );
}
